import React, { useEffect, useState } from 'react';
import { size, get, set } from 'global/lib/lodash';
import Modal from 'global/components/modal';
import { Typography } from '@material-ui/core';
import { useStyles } from './styles';
import Form from 'global/components/form';
import useRetrieveData from 'global/hooks/useRetrieveData';
import ScoreIcon from 'global/components/ScoreIcon';
import { useUser } from 'global/hooks/useUser';
import SnackAlerts from 'global/components/snack-alerts';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';

const CreateTaskWindow = (props) => {
  const { loading, formState, onFormUpdate, checkpoint, topContent } = props;
  const { me } = useUser();
  const classes = useStyles();

  const usersList = useRetrieveData(
    { url: '/users' },
    {
      build: ['users'],
      params: {
        filter: { content_realm_id: get(props, 'realmId') },
        include: 'realm',
      },
      useCache: { reload: 1 },
    }
  );

  useEffect(() => {
    if (!usersList.loading && props.assignedTo) {
      const atolist = props.assignedTo.map((a) => a.id);
      const users = usersList.data.filter((a) => atolist.indexOf(a.id) > -1);
      onFormUpdate({
        ...formState,
        clientId: get(users, '0.realm.id'),
      });
    }
  }, [usersList.data, usersList.loading]);

  const renderContent = () => {
    if (topContent) {
      return topContent();
    }
    if (checkpoint) {
      return (
        <div className={classes.rowTop}>
          <div className={classes.row}>
            <Typography variant="body2" className={classes.cpScoreHeaderChild}>
              {checkpoint.displayScore}
            </Typography>
            <ScoreIcon center score={checkpoint.value} />
          </div>
          <Typography variant="body2">
            <strong>{checkpoint.name}</strong>
            &nbsp;
            {!!checkpoint.criteria && checkpoint.criteria}
          </Typography>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderContent()}
      <Form
        loading={loading}
        showAllScreens={true}
        formStyle={classes.formStyle}
        onFormState={onFormUpdate}
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'description',
                type: 'TextField',
                defaultValue: () => get(checkpoint, 'notes'),
                props: {
                  placeholder: 'Describe your task',
                  rows: 5,
                  multiline: true,
                },
              },
              {
                id: 'assignedTo',
                type: 'UserSelect',
                defaultValue: () => props.assignedTo || [me],
                data: () => usersList.data,
                props: {
                  label: 'Assigned To',
                },
              },
            ],
          },
        ]}
      />
    </>
  );
};

const stringIt = (val) => {
  if (val == undefined || val == null) {
    return undefined;
  }
  return val.toString();
};

export const CreateModal = (props) => {
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState();
  const [formState, setFormState] = useState({});
  const { me } = useUser();
  const dispatch = useDispatch();

  const close = async (e, created = false) => {
    setFormState({});
    setAlerts();
    props.onClose && (await props.onClose(created));
    setLoading(false);
  };

  const formatData = async () => {
    const relationships = {};

    const isSubScore = get(props, 'checkpoint.isSubScore');

    const attributes = {
      name: !!props.taskName ? props.taskName : `${get(props, 'checklist.name', '')}: ${get(props, 'checkpoint.name')}`,
      description: get(formState, 'description'),
      status: 0,
      realm_id: stringIt(get(props, 'realmId')),
      assessment_id: stringIt(get(props, 'assessment.id')),
      scorecard_score_id: isSubScore ? undefined : stringIt(get(props, 'checkpoint.scorecardScoreId')),
      sub_score_id: isSubScore ? stringIt(get(props, 'checkpoint.id')) : undefined,
      checkpoint_id: stringIt(get(props, 'checkpoint.checkpointId')),
      client_id: stringIt(get(formState, 'clientId')),
      report_section_id: stringIt(get(props, 'highlight.id')),
    };

    const assignees = get(formState, 'assignedTo');
    set(relationships, 'assignees', {
      data: size(assignees) ? assignees.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
    });

    set(relationships, 'authors', {
      data: [{ type: 'users', role: 'author', id: get(me, 'id', '').toString() }],
    });

    return {
      data: {
        type: 'tasks',
        attributes,
        relationships,
      },
    };
  };

  const submit = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const data = await formatData();
      const createdTask = await sendData(
        {
          url: `/tasks?include=images,assignees,viewers,authors,comments,comments.author,comments.images,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions`,
          method: 'post',
          data,
        },
        { normalize: { build: ['tasks'] } }
      );
      await close(null, true);
      const task = get(createdTask, '0');
      dispatch(addNewTask(task));
    } catch (err) {
      setAlerts({
        message: `Failed to create task: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formUpdate = (vals) => {
    for (let k in vals) {
      set(formState, k, vals[k]);
    }
    setFormState({ ...formState });
  };

  const pProps = {
    title: 'Create Task',
    content: props.open ? <CreateTaskWindow {...props} loading={loading} formState={formState} onFormUpdate={formUpdate} /> : null,
    buttons: [
      { title: 'Cancel', onClick: close },
      { title: 'Submit', color: 'primary', variant: 'contained', onClick: submit },
    ],
    ...props,
    onClose: close,
  };

  return (
    <>
      <Modal {...pProps} />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </>
  );
};
